import { useState, useMemo, useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

import { usePerson } from "@/shared/hooks/useAppContext";
import { supabase } from "@/shared/lib/supabase";
import { trackEvent, EventType } from "@/shared/utils/core";

import type { EssentialModuleId } from "@/shared/types/estateEssentials";

import { getModuleById } from "@/shared/constants/estateEssentials";

import { useResolvedInitialData } from "./useResolvedInitialData";
import { useGuideMarkdown } from "./useGuideMarkdown";
import { useModuleFormSubmit } from "./useModuleFormSubmit";

export function useModuleDetail(moduleId: string | undefined) {
  const module = useMemo(
    () => (moduleId ? getModuleById(moduleId as EssentialModuleId) : undefined),
    [moduleId],
  );
  const isVehicleModule = moduleId === "vehicle-boat-titles";
  const person = usePerson();
  const queryClient = useQueryClient();

  const {
    initialData,
    isReady,
    shouldAutoOpen,
    taskStatus,
    deceasedData,
    userData,
    relationship,
    vehicleItems,
    addEstateItem,
    updateEstateItem,
  } = useResolvedInitialData(module, isVehicleModule);

  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (!isReady || !initialData) return;
    setFormData(initialData);
    if (shouldAutoOpen) setFormOpen(true);
  }, [isReady, initialData, shouldAutoOpen]);

  useEffect(() => {
    if (taskStatus !== null) setIsComplete(taskStatus === "completed");
  }, [taskStatus]);

  const guideMarkdown = useGuideMarkdown({
    module,
    formData,
    deceasedData,
    userFirstName: userData.firstName,
    userLastName: userData.lastName,
    relationship,
    isVehicleModule,
    vehicleItems,
  });

  const submitForm = useModuleFormSubmit({
    module,
    moduleId,
    deceasedUserId: deceasedData.deceasedUserId ?? undefined,
    authUserId: userData.supabaseData?.user_id,
    isVehicleModule,
    vehicleItems,
    addEstateItem,
    updateEstateItem,
  });

  const handleFormSubmit = useCallback(
    async (data: Record<string, string>) => {
      setFormData(data);
      setFormOpen(false);
      setSaveError(null);
      const error = await submitForm(data);
      if (error) {
        setSaveError(error);
      } else {
        trackEvent(EventType.ESSENTIALS_FORM_SUBMITTED, { module_id: moduleId });
      }
    },
    [submitForm, moduleId],
  );

  const handleFormOpenChange = useCallback(
    (open: boolean) => {
      if (!open) return;
      setFormOpen(open);
      trackEvent(EventType.ESSENTIALS_FORM_OPENED, { module_id: moduleId });
    },
    [moduleId],
  );

  const handleVehicleChange = useCallback((vehicleId: string) => {
    setFormData((prev) => ({ ...prev, selected_vehicle_id: vehicleId }));
    setFormOpen(true);
  }, []);

  const handleMarkComplete = useCallback(async () => {
    const newComplete = !isComplete;
    setIsComplete(newComplete);

    if (!module?.taskId || !person?.estate_id) return;

    const now = new Date().toISOString();
    const { error } = await supabase.from("estate_tasks").upsert(
      {
        task_id: module.taskId,
        estate_id: person.estate_id,
        status: newComplete ? "completed" : "in_progress",
        completed_at: newComplete ? now : null,
        last_active_at: now,
      },
      { onConflict: "estate_id,task_id" },
    );

    if (error) {
      console.error("Failed to update completion status:", error);
      setIsComplete(!newComplete);
      return;
    }

    trackEvent(EventType.ESSENTIALS_MODULE_COMPLETED, {
      module_id: moduleId,
      new_status: newComplete ? "completed" : "unmarked",
    });

    if (newComplete) {
      enqueueSnackbar("Task marked as complete", { variant: "success" });
    }

    await queryClient.invalidateQueries({
      queryKey: ["estateTaskAnswers", person.estate_id, module.taskId],
    });
    await queryClient.invalidateQueries({
      queryKey: ["moduleProgress", person.estate_id],
    });
    await queryClient.invalidateQueries({
      queryKey: ["elayneTasks", person.estate_id],
    });
  }, [isComplete, module?.taskId, person?.estate_id, queryClient, moduleId]);

  return {
    module,
    formOpen,
    setFormOpen,
    formData,
    saveError,
    guideMarkdown,
    deceasedData,
    userData,
    relationship,
    isVehicleModule,
    vehicleItems,
    isComplete,
    handleFormOpenChange,
    handleFormSubmit,
    handleVehicleChange,
    handleMarkComplete,
  };
}
